export type LotteryRoundRealtimePayload = {
  _id?: string
  lotteryRoundId?: string
  roundId?: string
  roundNumber?: string
  status?: string
  isOpen?: boolean
  newStatus?: string
  newIsOpen?: boolean
  betEndTime?: string
  isActive?: boolean
  results?: unknown
  result?: string
  [key: string]: unknown
}

export function normalizeRoundId(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'object' && value !== null && '_id' in (value as object)) {
    return normalizeRoundId((value as { _id?: unknown })._id)
  }
  return String(value).trim()
}

export function extractRoundFromRealtimeDetail(detail: unknown): LotteryRoundRealtimePayload | null {
  if (!detail || typeof detail !== 'object') return null
  const record = detail as Record<string, unknown>
  const round = record.round ?? record.data
  if (round && typeof round === 'object') return round as LotteryRoundRealtimePayload
  return null
}

export function resolveRoundIdFromPayload(round: LotteryRoundRealtimePayload | null | undefined): string {
  if (!round) return ''
  return normalizeRoundId(round._id ?? round.lotteryRoundId ?? round.roundId)
}

export function isLotteryRoundClosed(
  round: { status?: string; isOpen?: boolean; newStatus?: string; newIsOpen?: boolean } | null | undefined,
): boolean {
  if (!round) return false
  const normalized = applyRealtimeRoundFields(round)
  const isOpen = normalized.isOpen
  if (isOpen === false) return true
  const status = String(normalized.status ?? '').toLowerCase()
  return status === 'closed' || status === 'cancelled' || status === 'announced' || status === 'completed'
}

/** รวม newStatus/newIsOpen จาก websocket payload เข้ากับฟิลด์หลัก */
export function applyRealtimeRoundFields<T extends LotteryRoundRealtimePayload>(round: T): T {
  const statusRaw = round.status ?? round.newStatus
  const isOpenRaw = round.isOpen ?? round.newIsOpen
  return {
    ...round,
    ...(statusRaw !== undefined && statusRaw !== null && statusRaw !== '' && { status: String(statusRaw) }),
    ...(isOpenRaw !== undefined && { isOpen: Boolean(isOpenRaw) }),
  }
}

function roundHasAnyResult(
  round: { results?: unknown; result?: string } | null | undefined,
): boolean {
  if (!round) return false
  const results = round.results as { winningNumbers?: Record<string, unknown> } | undefined
  const wn = results?.winningNumbers
  if (wn && typeof wn === 'object') {
    return Object.values(wn).some((value) => value != null && String(value).trim() !== '')
  }
  return !!(round.result && String(round.result).trim())
}

/**
 * หน้าแสดงผลหวย — แสดงเฉพาะรอบที่ยังเปิดรับแทง มีผลแล้ว หรือยกเลิก
 * รอบที่ปิดรับแล้วแต่ยังไม่มีผลจะซ่อน (รวมกรณีแอดมินปิดรอบ) จนกว่าจะประกาศผลผ่าน websocket
 */
export function isRoundEligibleForResultsDisplay(
  round:
    | {
        status?: string
        isOpen?: boolean
        newStatus?: string
        newIsOpen?: boolean
        betEndTime?: string
        isActive?: boolean
        results?: unknown
        result?: string
      }
    | null
    | undefined,
): boolean {
  if (!round) return false
  const normalized = applyRealtimeRoundFields(round)
  if (normalized.isActive === false) return false

  const status = String(normalized.status ?? '').toLowerCase()
  if (status === 'cancelled') return true
  if (roundHasAnyResult(normalized)) return true
  if (status === 'announced' || status === 'completed') return true

  if (isLotteryRoundClosed(normalized)) return false

  return true
}

export function mergeRoundIntoList<T extends { _id?: string }>(list: T[], incomingRaw: T): T[] {
  const incoming = applyRealtimeRoundFields(incomingRaw as LotteryRoundRealtimePayload) as T
  const incomingId = normalizeRoundId(incoming._id)
  if (!incomingId) return list

  const isActive = (incoming as { isActive?: boolean }).isActive
  const index = list.findIndex((round) => normalizeRoundId(round._id) === incomingId)
  if (isActive === false) {
    if (index === -1) return list
    return list.filter((_, i) => i !== index)
  }
  if (index === -1) return list
  return list.map((round, i) => (i === index ? { ...round, ...incoming } : round))
}
